import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MessagesPanel = () => {
  const { user, userProfile } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState(null);

  // Enhanced UUID validation with better error handling
  const validateUUID = (id, context) => {
    if (!id) {
      console.warn(`${context}: ID is null or undefined`);
      return false;
    }
    
    if (typeof id !== 'string') {
      console.warn(`${context}: ID is not a string, got:`, typeof id, id);
      return false;
    }
    
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const isValid = uuidRegex?.test(id);
    
    if (!isValid) {
      console.error(`${context}: Invalid UUID format detected:`, {
        id,
        type: typeof id,
        length: id?.length,
        userObject: user
      });
    }
    
    return isValid;
  };

  // Load conversations
  useEffect(() => {
    if (!user?.id) {
      console.log('MessagesPanel: No user ID available, skipping conversations load');
      setLoading(false);
      setError('User not authenticated');
      return;
    }

    const loadConversations = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Enhanced UUID validation with detailed logging
        if (!validateUUID(user?.id, 'LoadConversations - user.id')) {
          setError(`Invalid user ID format: ${user?.id}. Please refresh the page or log in again.`);
          setConversations([]);
          return;
        }

        console.log('MessagesPanel: Loading conversations for user:', user?.id);

        const { data, error } = await supabase
          ?.from('conversations')
          ?.select(`
            *,
            client:client_id(id, full_name, avatar_url, role),
            practitioner:practitioner_id(id, full_name, avatar_url, role),
            messages(content, created_at)
          `)
          ?.or(`client_id.eq.${user?.id},practitioner_id.eq.${user?.id}`)
          ?.order('last_message_at', { ascending: false });

        if (error) {
          console.error('Error loading conversations:', error);
          setError(`Failed to load conversations: ${error?.message}`);
          return;
        }

        console.log('MessagesPanel: Successfully loaded conversations:', data?.length || 0);
        setConversations(data || []);
      } catch (error) {
        console.error('Conversations load error:', error);
        setError(`Unexpected error loading conversations: ${error?.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadConversations();

    // Set up real-time subscription for conversations
    const conversationChannel = supabase
      ?.channel('conversations')
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversations' },
        () => {
          loadConversations(); // Reload conversations on any change
        }
      )
      ?.subscribe();

    return () => {
      conversationChannel?.unsubscribe();
    };
  }, [user?.id]);

  // Load messages for selected conversation
  useEffect(() => {
    if (!selectedConversation?.id || !user?.id) return;

    const loadMessages = async () => {
      try {
        // Enhanced UUID validation
        if (!validateUUID(selectedConversation?.id, 'LoadMessages - conversation.id') || 
            !validateUUID(user?.id, 'LoadMessages - user.id')) {
          console.error('Invalid UUID format in loadMessages');
          return;
        }

        const { data, error } = await supabase
          ?.from('messages')
          ?.select(`
            *,
            sender:sender_id(id, full_name, avatar_url, role)
          `)
          ?.eq('conversation_id', selectedConversation?.id)
          ?.order('created_at', { ascending: true });

        if (error) {
          console.error('Error loading messages:', error);
          return;
        }

        setMessages(data || []);

        // Mark messages as read
        if (data?.length > 0) {
          await supabase
            ?.from('messages')
            ?.update({ 
              status: 'read',
              read_at: new Date()?.toISOString()
            })
            ?.eq('conversation_id', selectedConversation?.id)
            ?.neq('sender_id', user?.id)
            ?.eq('status', 'delivered');
        }
      } catch (error) {
        console.error('Messages load error:', error);
      }
    };

    loadMessages();

    // Set up real-time subscription for messages
    const messageChannel = supabase
      ?.channel(`messages-${selectedConversation?.id}`)
      ?.on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'messages',
          filter: `conversation_id=eq.${selectedConversation?.id}`
        },
        () => {
          loadMessages(); // Reload messages on any change
        }
      )
      ?.subscribe();

    return () => {
      messageChannel?.unsubscribe();
    };
  }, [selectedConversation?.id, user?.id]);

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage?.trim() || !selectedConversation?.id || !user?.id || sendingMessage) return;

    // Enhanced UUID validation before sending
    if (!validateUUID(selectedConversation?.id, 'SendMessage - conversation.id') || 
        !validateUUID(user?.id, 'SendMessage - user.id')) {
      console.error('Invalid UUID format in handleSendMessage');
      return;
    }

    setSendingMessage(true);
    try {
      const { error } = await supabase
        ?.from('messages')
        ?.insert({
          conversation_id: selectedConversation?.id,
          sender_id: user?.id,
          content: newMessage?.trim(),
          status: 'sent'
        });

      if (error) {
        console.error('Send message error:', error);
        return;
      }

      setNewMessage('');
    } catch (error) {
      console.error('Message send error:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.abs(now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date?.toLocaleDateString();
  };

  const getOtherParticipant = (conversation) => {
    if (!conversation || !user) return null;
    return conversation?.client_id === user?.id 
      ? conversation?.practitioner 
      : conversation?.client;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Enhanced error display
  if (error) {
    return (
      <div className="h-[600px] border border-border rounded-lg overflow-hidden">
        <div className="flex items-center justify-center h-full bg-muted/30">
          <div className="text-center text-red-600 max-w-md mx-auto p-4">
            <Icon name="AlertTriangle" size={48} className="mx-auto mb-4 opacity-50" />
            <h3 className="font-semibold mb-2">Unable to Load Messages</h3>
            <p className="text-sm mb-4">{error}</p>
            <Button
              onClick={() => {
                setError(null);
                window?.location?.reload();
              }}
              variant="outline"
              size="sm"
            >
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Refresh Page
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[600px] border border-border rounded-lg overflow-hidden">
      <div className="flex h-full">
        {/* Conversations List */}
        <div className="w-1/3 border-r border-border bg-muted/30">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold text-foreground">Messages</h3>
          </div>
          
          <div className="overflow-y-auto">
            {conversations?.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                <Icon name="MessageSquare" size={48} className="mx-auto mb-2 opacity-50" />
                <p>No conversations yet</p>
              </div>
            ) : (
              conversations?.map((conversation) => {
                const otherParticipant = getOtherParticipant(conversation);
                const lastMessage = conversation?.messages?.[conversation?.messages?.length - 1];
                
                return (
                  <button
                    key={conversation?.id}
                    onClick={() => setSelectedConversation(conversation)}
                    className={`w-full p-4 text-left border-b border-border hover:bg-accent/50 transition-colors ${
                      selectedConversation?.id === conversation?.id ? 'bg-accent' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        {otherParticipant?.avatar_url ? (
                          <img 
                            src={otherParticipant?.avatar_url} 
                            alt={otherParticipant?.full_name}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <Icon name="User" size={18} className="text-primary" />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm text-foreground truncate">
                            {otherParticipant?.full_name || 'Unknown User'}
                          </p>
                          <span className="text-xs text-muted-foreground">
                            {formatTime(conversation?.last_message_at)}
                          </span>
                        </div>
                        
                        <p className="text-xs text-primary capitalize mb-1">
                          {otherParticipant?.role || 'user'}
                        </p>
                        
                        <p className="text-sm font-medium text-foreground truncate mb-1">
                          {conversation?.subject}
                        </p>
                        
                        {lastMessage && (
                          <p className="text-xs text-muted-foreground truncate">
                            {lastMessage?.content}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Messages View */}
        <div className="flex-1 flex flex-col">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-border bg-background">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    {getOtherParticipant(selectedConversation)?.avatar_url ? (
                      <img 
                        src={getOtherParticipant(selectedConversation)?.avatar_url} 
                        alt={getOtherParticipant(selectedConversation)?.full_name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <Icon name="User" size={18} className="text-primary" />
                    )}
                  </div>
                  
                  <div>
                    <p className="font-semibold text-foreground">
                      {getOtherParticipant(selectedConversation)?.full_name || 'Unknown User'}
                    </p>
                    <p className="text-sm text-primary capitalize">
                      {getOtherParticipant(selectedConversation)?.role || 'user'}
                    </p>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground mt-2">
                  Subject: {selectedConversation?.subject}
                </p>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages?.map((message) => {
                  const isOwnMessage = message?.sender_id === user?.id;
                  
                  return (
                    <div
                      key={message?.id}
                      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${
                        isOwnMessage 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted text-foreground'
                      } rounded-lg px-4 py-2`}>
                        <p className="text-sm">{message?.content}</p>
                        <p className={`text-xs mt-1 ${
                          isOwnMessage ? 'text-primary-foreground/70' : 'text-muted-foreground'
                        }`}>
                          {formatTime(message?.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-border">
                <div className="flex space-x-2">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e?.target?.value)}
                    placeholder="Type your message..."
                    rows={2}
                    className="flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    onKeyDown={(e) => {
                      if (e?.key === 'Enter' && !e?.shiftKey) {
                        e?.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                  />
                  <Button
                    type="submit"
                    loading={sendingMessage}
                    disabled={!newMessage?.trim()}
                    size="icon"
                    className="self-end"
                  >
                    <Icon name="Send" size={16} />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-muted/30">
              <div className="text-center text-muted-foreground">
                <Icon name="MessageSquare" size={48} className="mx-auto mb-4 opacity-50" />
                <p>Select a conversation to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesPanel;